import type { EditorField } from '../editor/editorStore';
import type { CreateEnvelopeBody, InlineField, InputType, PageSize } from './types';

/**
 * Quick send: the admin uploads their own PDFs, places signer fields and sends
 * them to ONE contact — no template. Pure helpers here; state lives in
 * pages/quick-send/quickSendStore.ts.
 */

/** The one signer role. The server uses an upload role's key as its label too. */
export const QUICK_SEND_ROLE = 'signer';
export const MAX_DOCUMENTS = 10;
export const DEFAULT_SUBJECT = 'Documents for you to sign';
/** Mirrors ENVELOPE_SUBJECT_MAX / ENVELOPE_MESSAGE_MAX in the API's create-envelope DTO. */
export const SUBJECT_MAX = 200;
export const MESSAGE_MAX = 2000;

/** Starting size (points) per field type. */
export const DEFAULT_FIELD_SIZE: Record<InputType, { w: number; h: number }> = {
  signature: { w: 180, h: 48 },
  initials: { w: 90, h: 40 },
  date: { w: 120, h: 24 },
  text: { w: 180, h: 24 },
  checkbox: { w: 18, h: 18 },
};

export type UploadState = 'uploading' | 'ready' | 'failed';

export interface QuickSendDoc {
  /** Client-only id (the upload id arrives later, or never). */
  id: string;
  file: File;
  title: string;
  state: UploadState;
  error?: string;
  uploadId?: string;
  /** From the server's upload response — the sizes the API bounds-checks against. */
  pageSizes: PageSize[];
  fields: EditorField[];
}

export interface QuickSendDraft {
  docs: QuickSendDoc[];
  name: string;
  email: string;
  subject: string;
  message: string;
  /** Envelope title; blank → defaultTitle(name). */
  title: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** "Quote QU-0042.pdf" → "Quote QU-0042". */
export function titleFromFilename(filename: string): string {
  return filename.replace(/\.[a-z0-9]{1,5}$/i, '').trim() || 'Document';
}

/** The envelope title the Envelopes list (and n8n's Drive folder) shows. */
export function defaultTitle(name: string): string {
  return name.trim() ? `Quick send - ${name.trim()}` : 'Quick send';
}

type Box = { x: number; y: number; w: number; h: number };

/** Keep a box on its page — the API rejects any field past the page edge. */
export function clampToPage<T extends Box>(box: T, size: PageSize): T {
  const w = Math.min(Math.max(1, box.w), size.width);
  const h = Math.min(Math.max(1, box.h), size.height);
  return {
    ...box,
    w,
    h,
    x: Math.min(Math.max(0, box.x), size.width - w),
    y: Math.min(Math.max(0, box.y), size.height - h),
  };
}

/**
 * A new signer field near the top-left of the page, stepped down-right by how
 * many fields the page already has so repeated clicks don't stack exactly.
 * Defaults match the template editor: required, auto date, optional checkbox.
 */
export function newField(
  type: InputType,
  page: number,
  size: PageSize,
  alreadyOnPage: number,
  uid: string,
): EditorField {
  const s = DEFAULT_FIELD_SIZE[type];
  const step = (alreadyOnPage % 8) * 28;
  return clampToPage(
    {
      uid,
      kind: 'input',
      page,
      x: 72 + step,
      y: 72 + step,
      w: s.w,
      h: s.h,
      type,
      signerRole: QUICK_SEND_ROLE,
      required: true,
      repeatPerPage: false,
      ...(type === 'date' ? { dateMode: 'auto' as const } : {}),
      ...(type === 'checkbox' ? { mustCheck: false } : {}),
    },
    size,
  );
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Only the keys whose value is set — undefined means "inherit" on the server. */
function defined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;
}

/**
 * Editor field → the API's InlineFieldDto: points, top-left origin. Rounded to
 * 2dp (whole-point rounding of x and w separately can overshoot the page edge
 * by a point, which the API rejects), then clamped again.
 */
export function toInlineField(f: EditorField, size: PageSize): InlineField {
  const box = clampToPage({ x: r2(f.x), y: r2(f.y), w: r2(f.w), h: r2(f.h) }, size);
  return {
    type: f.type ?? 'text',
    signerRole: QUICK_SEND_ROLE,
    page: f.page,
    ...box,
    unit: 'pt',
    origin: 'top-left',
    required: f.required ?? true,
    ...defined({
      repeatPerPage: f.repeatPerPage,
      dateMode: f.type === 'date' ? f.dateMode : undefined,
      maxLen: f.type === 'text' ? f.maxLen : undefined,
      mustCheck: f.type === 'checkbox' ? f.mustCheck : undefined,
      font: f.font,
      fontSize: f.fontSize,
      fontColor: f.fontColor,
      align: f.align,
    }),
  };
}

/**
 * The POST /v1/envelopes body. List order IS signing order (1..n), so it beats
 * any Settings keyword rule — what the admin sees is what the contact gets.
 */
export function toEnvelopeRequest(d: QuickSendDraft): CreateEnvelopeBody {
  const subject = d.subject.trim();
  const message = d.message.trim();
  return {
    title: d.title.trim() || defaultTitle(d.name),
    ...(subject ? { subject } : {}),
    ...(message ? { message } : {}),
    documents: d.docs.map((doc, i) => ({
      uploadId: doc.uploadId ?? '',
      title: doc.title.trim() || titleFromFilename(doc.file.name),
      signingOrder: i + 1,
      fields: doc.fields
        .slice()
        .sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x)
        .map((f) => toInlineField(f, doc.pageSizes[f.page - 1])),
    })),
    signers: [{ role: QUICK_SEND_ROLE, name: d.name.trim(), email: d.email.trim() }],
  };
}

/** Errors block the send; warnings are shown on the confirm step. */
export function checkQuickSend(d: QuickSendDraft): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (d.docs.length === 0) errors.push('Add at least one PDF.');
  if (d.docs.some((doc) => doc.state === 'uploading')) errors.push('Wait for the uploads to finish.');
  if (d.docs.some((doc) => doc.state === 'failed')) {
    errors.push('Remove the documents that failed to upload.');
  }
  const fields = d.docs.flatMap((doc) => doc.fields);
  if (d.docs.length > 0 && fields.length === 0) {
    errors.push('Place at least one field for the contact to fill in.');
  }
  if (!d.name.trim()) errors.push("Enter the contact's name.");
  if (!EMAIL_RE.test(d.email.trim())) errors.push('Enter a valid email address.');
  if (d.subject.trim().length > SUBJECT_MAX) {
    errors.push(`Keep the subject under ${SUBJECT_MAX} characters.`);
  }
  if (d.message.trim().length > MESSAGE_MAX) {
    errors.push(`Keep the note under ${MESSAGE_MAX} characters.`);
  }

  for (const doc of d.docs) {
    if (doc.state === 'ready' && doc.fields.length === 0) {
      warnings.push(`"${doc.title || doc.file.name}" has no fields — the contact will only read it.`);
    }
  }
  if (fields.length > 0 && !fields.some((f) => f.type === 'signature' || f.type === 'initials')) {
    warnings.push('There is no signature or initials field anywhere.');
  }
  return { errors, warnings };
}
